import { useState, useCallback } from 'react';
import { moveAreaIndicator } from '../../../services/b2b-plans-apiService';
import { useLoginContext } from '../../../App';

/**
 * Hook to manage the move of area indicators
 * @returns {Object} Object containing move function, loading state, error state, and success state
 */
const useMoveAreaIndicator = () => {
    const { loginData } = useLoginContext();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [isSuccess, setIsSuccess] = useState(false);

    /**
     * Move an area indicator from source to target area
     * @param {string} sourcePlanUUID - UUID of the source plan
     * @param {string} sourceAreaId - ID of the source area
     * @param {string} areaIndicatorId - ID of the area indicator to move
     * @param {string} targetPlanUUID - UUID of the target plan
     * @param {string} targetAreaId - ID of the target area
     * @returns {Promise<Object>} Promise that resolves with the move result
     */
    const moveAreaIndicatorWithTiming = useCallback(async (
        sourcePlanUUID,
        sourceAreaId,
        areaIndicatorId,
        targetPlanUUID,
        targetAreaId
    ) => {
        setLoading(true);
        setError(null);
        setIsSuccess(false);

        try {
            const result = await moveAreaIndicator(
                loginData.accessToken,
                sourcePlanUUID,
                sourceAreaId,
                areaIndicatorId,
                targetPlanUUID,
                targetAreaId
            );

            setIsSuccess(true);

            return {
                success: true,
                result
            };
        } catch (err) {
            console.error('Error moving area indicator:', err);
            setError(err);

            return {
                success: false,
                error: err
            };
        } finally {
            setLoading(false);
        }
    }, [loginData.accessToken]);

    /**
     * Reset the hook state
     */
    const resetState = useCallback(() => {
        setLoading(false);
        setError(null);
        setIsSuccess(false);
    }, []);

    return {
        moveAreaIndicator: moveAreaIndicatorWithTiming,
        loading,
        error,
        isSuccess,
        resetState
    };
};

export default useMoveAreaIndicator;
